export type MicStatus =
  | 'ok'
  | 'muted'
  | 'no-input'
  | 'blocked'
  | 'missing'
  | 'error'
export type CameraStatus = 'ok' | 'off' | 'blocked' | 'missing' | 'error'

// Agora network quality: 0 unknown, 1 excellent ... 5 very bad, 6 down.
export type NetworkQuality = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type DeviceStatus = {
  agoraConnected: boolean | null
  micStatus: MicStatus | null
  cameraStatus: CameraStatus | null
  networkQualityUp: number | null
  networkQualityDown: number | null
  sendBitrateKbps: number | null
  recvBitrateKbps: number | null
  rttMs: number | null
  statusUpdatedAt: string | null
}

export type DeviceStatusSnapshot = Omit<DeviceStatus, 'statusUpdatedAt'>

export type HealthLevel = 'unknown' | 'ok' | 'warning' | 'critical'

export type DeviceIssueCode =
  | 'micUnavailable'
  | 'micNoInput'
  | 'cameraUnavailable'
  | 'notConnected'
  | 'notReporting'
  | 'networkUplinkPoor'
  | 'networkUplinkDown'
  | 'networkDownlinkPoor'
  | 'networkDownlinkDown'

export type DeviceIssue = {
  code: DeviceIssueCode
  level: 'warning' | 'critical'
}

export type DeviceHealth = {
  level: HealthLevel
  issues: DeviceIssue[]
  status: DeviceStatus | null
}

export const STALE_AFTER_MS = 90 * 1000

const POOR_QUALITY = 3
const DOWN_QUALITY = 5

const LEVEL_RANK: Record<HealthLevel, number> = {
  unknown: 0,
  ok: 1,
  warning: 2,
  critical: 3,
}

export const healthRank = (level: HealthLevel) => LEVEL_RANK[level]

// Column names as they will exist on live_call_state once added in the Hasura backend.
export type DeviceStatusColumns = {
  agora_connected?: boolean | null
  mic_status?: string | null
  camera_status?: string | null
  network_quality_up?: number | null
  network_quality_down?: number | null
  send_bitrate_kbps?: number | null
  recv_bitrate_kbps?: number | null
  rtt_ms?: number | null
  status_updated_at?: string | null
}

export const readDeviceStatus = (
  columns: DeviceStatusColumns,
): DeviceStatus | null => {
  if (!columns.status_updated_at) return null

  return {
    agoraConnected: columns.agora_connected ?? null,
    micStatus: (columns.mic_status as MicStatus | null | undefined) ?? null,
    cameraStatus:
      (columns.camera_status as CameraStatus | null | undefined) ?? null,
    networkQualityUp: columns.network_quality_up ?? null,
    networkQualityDown: columns.network_quality_down ?? null,
    sendBitrateKbps: columns.send_bitrate_kbps ?? null,
    recvBitrateKbps: columns.recv_bitrate_kbps ?? null,
    rttMs: columns.rtt_ms ?? null,
    statusUpdatedAt: columns.status_updated_at,
  }
}

const worst = (levels: HealthLevel[]): HealthLevel =>
  levels.reduce<HealthLevel>(
    (acc, level) => (healthRank(level) > healthRank(acc) ? level : acc),
    'unknown',
  )

export type DeriveDeviceHealthOptions = {
  now: number
  isPresent: boolean
}

export function deriveDeviceHealth(
  status: DeviceStatus | null,
  { now, isPresent }: DeriveDeviceHealthOptions,
): DeviceHealth {
  if (!status || !status.statusUpdatedAt) {
    return { level: 'unknown', issues: [], status }
  }

  const updatedAt = new Date(status.statusUpdatedAt).getTime()
  if (Number.isFinite(updatedAt) && now - updatedAt > STALE_AFTER_MS) {
    return {
      level: 'critical',
      issues: [{ code: 'notReporting', level: 'critical' }],
      status,
    }
  }

  const issues: DeviceIssue[] = []

  if (status.agoraConnected === false && isPresent) {
    issues.push({ code: 'notConnected', level: 'critical' })
  }

  if (['blocked', 'missing', 'error'].includes(status.micStatus ?? '')) {
    issues.push({ code: 'micUnavailable', level: 'critical' })
  } else if (status.micStatus === 'no-input') {
    issues.push({ code: 'micNoInput', level: 'warning' })
  }

  if (['blocked', 'missing', 'error'].includes(status.cameraStatus ?? '')) {
    issues.push({ code: 'cameraUnavailable', level: 'critical' })
  }

  const up = status.networkQualityUp ?? 0
  if (up >= DOWN_QUALITY) {
    issues.push({ code: 'networkUplinkDown', level: 'critical' })
  } else if (up >= POOR_QUALITY) {
    issues.push({ code: 'networkUplinkPoor', level: 'warning' })
  }

  const down = status.networkQualityDown ?? 0
  if (down >= DOWN_QUALITY) {
    issues.push({ code: 'networkDownlinkDown', level: 'critical' })
  } else if (down >= POOR_QUALITY) {
    issues.push({ code: 'networkDownlinkPoor', level: 'warning' })
  }

  return {
    level: issues.length > 0 ? worst(issues.map((i) => i.level)) : 'ok',
    issues,
    status,
  }
}
